import React, { useState } from 'react';
import { Sun, CloudRain, Crown, Sparkles } from 'lucide-react';
import { MysteryType } from '../../../types';
import { RosaryIcon } from '../../icons/RosaryIcon';

interface SetupDailyRosaryScreenProps {
  onContinue: (data: {
    selectedMystery: MysteryType;
    reminderTime: string;
    enableReminders: boolean;
  }) => void;
}

export const SetupDailyRosaryScreen: React.FC<SetupDailyRosaryScreenProps> = ({ onContinue }) => {
  const [selectedMystery, setSelectedMystery] = useState<MysteryType | null>(null);
  const [reminderTime, setReminderTime] = useState('19:00'); // 7:00 PM default
  const [enableReminders, setEnableReminders] = useState(true);

  const getTodaysMystery = (): MysteryType => {
    const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
    switch (today) {
      case 1: case 6: return 'Joyful';    // Monday, Saturday
      case 2: case 5: return 'Sorrowful'; // Tuesday, Friday
      case 3: case 0: return 'Glorious';  // Wednesday, Sunday
      case 4: return 'Luminous';          // Thursday
      default: return 'Joyful';
    }
  };

  const getDayName = (mystery: MysteryType): string => {
    switch (mystery) {
      case 'Joyful': return 'Mondays & Saturdays';
      case 'Sorrowful': return 'Tuesdays & Fridays';
      case 'Glorious': return 'Wednesdays & Sundays';
      case 'Luminous': return 'Thursdays';
      default: return '';
    }
  };

  const getMysteryIcon = (mystery: MysteryType) => {
    switch (mystery) {
      case 'Joyful': return <Sun className="w-6 h-6 text-yellow-500" />;
      case 'Sorrowful': return <CloudRain className="w-6 h-6 text-red-500" />;
      case 'Glorious': return <Crown className="w-6 h-6 text-indigo-500" />;
      case 'Luminous': return <Sparkles className="w-6 h-6 text-blue-500" />;
      default: return <Sun className="w-6 h-6 text-gray-500" />;
    }
  };

  const getMysteryDescription = (mystery: MysteryType): string => {
    switch (mystery) {
      case 'Joyful': return 'Celebrate the joyful events in the lives of Jesus and Mary';
      case 'Sorrowful': return 'Meditate on the suffering and passion of Our Lord';
      case 'Glorious': return 'Rejoice in the resurrection and triumph of Christ';
      case 'Luminous': return 'Contemplate the public ministry of Jesus Christ';
      default: return '';
    }
  };

  const todaysMystery = getTodaysMystery();
  const mysteries: MysteryType[] = ['Joyful', 'Sorrowful', 'Glorious', 'Luminous'];

  const handleContinue = () => {
    if (selectedMystery) {
      onContinue({
        selectedMystery,
        reminderTime,
        enableReminders,
      });
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mb-4 flex justify-center">
          <RosaryIcon size={64} className="text-indigo-600 dark:text-indigo-400" />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Choose your first rosary
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          Select which mystery you'd like to pray
        </p>
      </div>

      {/* Mystery Selection */}
      <div className="space-y-4 flex-1 overflow-y-auto">
        {mysteries.map((mystery) => {
          const isToday = mystery === todaysMystery;
          const isSelected = selectedMystery === mystery;

          return (
            <button
              key={mystery}
              onClick={() => setSelectedMystery(mystery)}
              className={`
                w-full p-4 rounded-xl text-left transition-all duration-200 border-2 relative
                ${
                  isSelected
                    ? 'border-indigo-600 ring-2 ring-indigo-600/20 bg-white dark:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-indigo-400 dark:hover:border-indigo-500'
                }
              `}
            >
              {/* Today badge */}
              {isToday && (
                <div className="absolute -top-2 right-4">
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                    ✨ Traditional for today
                  </span>
                </div>
              )}

              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="flex-shrink-0 mt-1">
                  {getMysteryIcon(mystery)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1">
                    {mystery} Mysteries
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {getMysteryDescription(mystery)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {getDayName(mystery)}
                  </p>
                </div>

                {/* Selection indicator */}
                {isSelected && (
                  <div className="flex-shrink-0 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          );
        })}

        {/* Prayer Reminders */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border-2 border-gray-200 dark:border-gray-700 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Daily prayer reminders
            </h3>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={enableReminders}
                onChange={(e) => setEnableReminders(e.target.checked)}
                className="w-5 h-5 rounded border-2 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-2 focus:ring-indigo-600/20 transition-all duration-200"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable reminders
              </span>
            </label>
          </div>

          {enableReminders && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                When would you like to pray?
              </label>
              <input
                type="time"
                value={reminderTime}
                onChange={(e) => setReminderTime(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 transition-all duration-200"
              />
            </div>
          )}
        </div>
      </div>

      {/* Continue Button */}
      <div className="mt-8">
        <button
          onClick={handleContinue}
          disabled={!selectedMystery}
          className={`
            w-full py-4 rounded-xl font-semibold text-lg transition-all duration-200 transform
            ${
              selectedMystery
                ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white hover:scale-105 shadow-lg'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Begin Praying
        </button>
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-3">
          You can change this anytime in settings
        </p>
      </div>
    </div>
  );
};
